import { Box, Button, Stack, ThemeProvider, Typography } from "@mui/material";
import { buildTheme, THEMES, type ColorMode, type ThemeId } from "@/theme";
import { StatusPill } from "@/components/StatusPill";

/**
 * A miniature of the real UI in one theme: a primary button, three status pills, a KPI figure and
 * three table rows.
 *
 * It renders inside its own nested `ThemeProvider` so it shows the theme it is *about*, not the one
 * the surrounding page is using. That is the whole point — an admin choosing a house style needs to
 * see the palettes they are not currently running.
 *
 * Everything here is a real component reading real tokens (`StatusPill`, `Button`, `divider`,
 * `text.secondary`), so the preview cannot drift from what the app actually renders. A hand-drawn
 * swatch would go stale the first time a token moved.
 */
export function ThemeSpecimen({
  themeId,
  mode,
  compact = false,
}: {
  themeId: ThemeId;
  mode: ColorMode;
  /** Drops the table rows — for tight rows of five specimens side by side. */
  compact?: boolean;
}) {
  const theme = buildTheme(themeId, mode, "comfortable");
  const def = THEMES[themeId];

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.default",
          display: "flex",
          minHeight: compact ? 132 : 190,
        }}
      >
        {/* Sidebar band — the shell is a big part of what makes a theme recognisable. */}
        <Box sx={{ width: 10, bgcolor: theme.shell.bg, flexShrink: 0 }} />

        <Box sx={{ flex: 1, minWidth: 0, p: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
              {def.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
              {mode === "dark" ? "Dark" : "Light"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button size="small" variant="contained">
              Save
            </Button>
            <StatusPill label="Posted" />
            <StatusPill label="Pending" />
            <StatusPill label="Cancelled" />
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Outstanding
            </Typography>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, lineHeight: 1.2 }}>
              1,248,300.00
            </Typography>
          </Box>

          {!compact && (
            <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1 }}>
              {["INV-24817", "INV-24816", "INV-24815"].map((no, i) => (
                <Box
                  key={no}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    px: 1,
                    py: 0.5,
                    fontSize: "0.75rem",
                    borderTop: i === 0 ? 0 : 1,
                    borderColor: "divider",
                  }}
                >
                  <Box component="span" sx={{ color: "text.secondary" }}>{no}</Box>
                  <Box component="span">{(4820.5 - i * 311).toFixed(2)}</Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
